import { useSuspenseQuery } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { RootRoute } from '..'
import {
  AuthenticateOidcDocument,
  GetConfigVarsDocument,
  MeDocument,
  OidcAccountDocument,
  PersistAccessTokenDocument,
} from '../../generated/graphql'
import { REDIRECT_QUERY_PARAM } from '../../utils/constants'
import {
  getBaseHostname,
  hasAccountIdOrAliasInHost,
  AUTH_REDIRECT_TOKEN_COOKIE,
} from '../login/util'
import { useCookie, useLocalStorage } from 'react-use'
import { getErrorMessages } from '../../utils/error-mapping'
import { useTracking } from '../../hooks/tracking'
import { useMe } from '../../hooks/me'
import { useAbsolutePath } from '../../hooks/absolute-path'

export const OidcCallbackPage = () => {
  const { me } = useMe()
  const { t } = useTranslation(['shared', 'login'])
  const { enqueueSnackbar } = useSnackbar()
  const { track } = useTracking()
  const [redirectPage] = useLocalStorage(REDIRECT_QUERY_PARAM)
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')
  const hasOidcError = searchParams.has('error')
  const navigate = useNavigate()
  const state = searchParams.get('state')
  const generatePath = useAbsolutePath()
  const [token, writeToken] = useCookie(AUTH_REDIRECT_TOKEN_COOKIE)

  const { data: authenticateData, error: authError } = useSuspenseQuery(
    AuthenticateOidcDocument,
    {
      variables: { code: String(code), state: String(state) },
      errorPolicy: 'all',
      skip: hasAccountIdOrAliasInHost(),
    },
  )

  const { data: oidcAccountData, error: oidcAccountError } = useSuspenseQuery(
    OidcAccountDocument,
    {
      variables: { state: String(state) },
      skip: !authenticateData,
      errorPolicy: 'all',
      context: {
        headers: {
          Authorization: authenticateData?.authenticateOidc,
        },
      },
    },
  )

  const { data: persistAccessTokenData, error: persistAccessTokenError } =
    useSuspenseQuery(PersistAccessTokenDocument, {
      variables: {
        accessToken: token!,
      },
      skip: !token || !hasAccountIdOrAliasInHost(),
      errorPolicy: 'all',
      fetchQueries: [{ query: MeDocument }, { query: GetConfigVarsDocument }],
    })

  const hasError =
    oidcAccountError || authError || hasOidcError || persistAccessTokenError

  // Redirect to the Logout page in case of error
  useEffect(() => {
    if (!hasError) return

    if (authError) {
      const errorMessage = getErrorMessages(authError, {
        format: (code, property) =>
          t(`login:error.${code}`, {
            property,
            host: getBaseHostname(),
            defaultValue: t('login:error.fallback_error'),
          }),
      })[0]

      track('authentication_failed', {
        category: 'authentication',
        name: 'OIDC authentication',
      })

      enqueueSnackbar(errorMessage, {
        variant: 'error',
        preventDuplicate: true,
      })
    }

    void navigate({ pathname: RootRoute.Logout })
  }, [authError, enqueueSnackbar, hasError, navigate, t, track])

  // Replace accountId in the URL
  useEffect(() => {
    if (!oidcAccountData) return

    const { accountAlias, accountId } = oidcAccountData.oidcAccount
    const subDomain = accountAlias ?? accountId?.toString()
    if (!hasAccountIdOrAliasInHost() && subDomain) {
      const newUrl = window.location.href.replace(
        'https://',
        `https://${subDomain}.`,
      )
      const token = authenticateData?.authenticateOidc ?? ''

      const oneMinuteFromNow = new Date(Date.now() + 60000)
      writeToken(token, {
        domain: getBaseHostname(),
        expires: oneMinuteFromNow,
      })

      window.location.assign(newUrl)
    }
  }, [oidcAccountData, authenticateData?.authenticateOidc, writeToken])

  // Redirect to the login page if there are no OIDC parameters
  useEffect(() => {
    if (!hasError && !state && !code) {
      void navigate(RootRoute.Login)
    }
  }, [code, hasError, navigate, state])

  // Redirect to the login page after the successful OIDC authentication
  // Login page would check if session exists and redirect to the home page
  useEffect(() => {
    if (persistAccessTokenData?.persistAccessToken && me) {
      track('authentication_succeeded', {
        category: 'authentication',
        name: 'OIDC authentication',
      })
      void navigate(redirectPage ?? generatePath(RootRoute.Home))
    }
  }, [
    navigate,
    redirectPage,
    track,
    me,
    persistAccessTokenData?.persistAccessToken,
    generatePath,
  ])

  return null
}
